// src/components/modals/InvoiceGenerationModal.js
import React, { useState, useEffect } from "react";
import { X, CheckCircle, AlertTriangle, Building2, Truck, FileText, Send, User, MapPin, Trash2, Plus } from "lucide-react";
import { fiscalService } from "../../services/fiscalService";
import { pb } from "../../services/pocketbase";


export default function InvoiceGenerationModal({
  isOpen,
  onClose,
  orderGroup,
  invoice,
  findCatalogItem,
  onSuccess,
  isAvulso = false,
  forcedTipoNota = null,
}) {
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [naturezaOperacao, setNaturezaOperacao] = useState("Venda de mercadoria");

  // --- ESTADOS AUXILIARES PARA IMPORTAÇÃO DE PEDIDO EM EMISSÃO AVULSA ---
  const [avulsoTipoNota, setAvulsoTipoNota] = useState("venda");
  const [nfeReferenciada, setNfeReferenciada] = useState("");
  const [searchedOrderId, setSearchedOrderId] = useState("");
  const [searchedOrderNumber, setSearchedOrderNumber] = useState("");
  const [searchOrderQuery, setSearchOrderQuery] = useState("");
  const [searchingOrder, setSearchingOrder] = useState(false);
  const [searchingReferencedKey, setSearchingReferencedKey] = useState(false);
  // Busca detalhes de um pedido real para autocompletar o formulário avulso
  const fetchOrderDetailsForAvulso = async (orderNum) => {
    if (!orderNum || !orderNum.trim()) return;
    try {
      setSearchingOrder(true);
      const orders = await pb.collection("production_orders").getList(1, 50, {
        filter: `order_number = "${orderNum.trim()}"`,
        expand: "fiscal_invoices_via_order",
      });
      if (orders.items.length === 0) {
        alert("Nenhum pedido encontrado com este número.");
        return;
      }

      const firstItem = orders.items[0];

      let fullData = {};
      if (firstItem.full_data) {
        if (typeof firstItem.full_data === "string") {
          try {
            fullData = JSON.parse(firstItem.full_data);
          } catch (e) {
            console.error("Erro ao fazer parse de full_data:", e);
          }
        } else {
          fullData = firstItem.full_data;
        }
      }

      const customer = firstItem.order?.customer || fullData.order?.customer || fullData.customer || {};
      const shippingAddress = firstItem.shipping?.modified_address || firstItem.shipping?.address || fullData.shipping?.modified_address || fullData.shipping?.address || customer.address || {};

      setSearchedOrderId(firstItem.id);
      setSearchedOrderNumber(firstItem.order_number || fullData.orderNumber || fullData.order?.number || orderNum.trim());

      setDestData({
        nome: customer.name || firstItem.customer_name || "",
        cpf_cnpj: customer.cpf || customer.cnpj || "",
        email: customer.email || "",
        telefone: customer.phone || "",
        cep: shippingAddress.zip || shippingAddress.cep || "",
        logradouro: shippingAddress.street || shippingAddress.logradouro || "",
        numero: shippingAddress.number || shippingAddress.numero || "",
        complemento: shippingAddress.complemento || "",
        bairro: shippingAddress.district || shippingAddress.bairro || "",
        cidade: shippingAddress.city || shippingAddress.cidade || "",
        uf: (shippingAddress.statecode || shippingAddress.state || "").toUpperCase(),
        ie: customer.ie || "",
        indicador_ie: customer.cpf ? "9" : (customer.cnpj ? "9" : "9"),
      });

      // Busca por notas fiscais autorizadas vinculadas ao pedido para referenciar a chave automaticamente
      setSearchingReferencedKey(true);
      try {
        const invoiceRes = await pb.collection("fiscal_invoices").getList(1, 1, {
          filter: `order_number = "${orderNum.trim()}" && nfe_status = "autorizada"`,
          sort: "-created",
        });
        if (invoiceRes.items.length > 0) {
          setNfeReferenciada(invoiceRes.items[0].nfe_chave || "");
        } else if (firstItem.expand?.fiscal_invoices_via_order) {
          const authInv = firstItem.expand.fiscal_invoices_via_order.find(i => i.nfe_status === "autorizada");
          if (authInv) {
            setNfeReferenciada(authInv.nfe_chave || "");
          }
        }
      } catch (err) {
        console.warn("Erro ao buscar nota referenciada:", err);
      } finally {
        setSearchingReferencedKey(false);
      }

      // Mapeia itens do pedido
      const mappedItems = orders.items.map((item, index) => {
        let itemFullData = {};
        if (item.full_data) {
          if (typeof item.full_data === "string") {
            try {
              itemFullData = JSON.parse(item.full_data);
            } catch (e) {}
          } else {
            itemFullData = item.full_data;
          }
        }

        const specs = item.specs || itemFullData.specs || {};
        const catalogData = findCatalogItem ? findCatalogItem(item.sku) : null;
        let desc = catalogData?.name || itemFullData.name || item.sku || "Produto";
        if (specs.size) desc += ` (Aro ${specs.size})`;

        // Determina o valor do item
        let itemPrice = parseFloat(item.total_value || itemFullData.price || catalogData?.price || 0);
        
        // Soma os valores adicionais dos specs (ex: "45cm - R$ 45,00")
        if (specs) {
          Object.values(specs).forEach((specVal) => {
            if (typeof specVal === "string") {
              const match = specVal.match(/R\$\s*([0-9.,]+)/);
              if (match) {
                const valStr = match[1].replace(/\./g, "").replace(",", ".");
                itemPrice += parseFloat(valStr) || 0;
              }
            }
          });
        }

        return {
          id: item.id || Date.now() + Math.random(),
          numero_item: index + 1,
          codigo_produto: item.sku || "ND",
          descricao: desc,
          quantidade_comercial: item.quantity || itemFullData.quantity || 1,
          valor_unitario_comercial: itemPrice,
          valor_bruto: itemPrice,
          ncm: catalogData?.ncm || item.ncm || "71131100",
          cfop: "5102",
          origem: 0,
          csosn: "102",
          pis_cst: "07",
          cofins_cst: "07",
        };
      });

      // Se houver apenas 1 item e o preço dele veio zerado, tenta deduzir do valor total do pedido
      const totalPayment = parseFloat(fullData.order?.payment?.total || fullData.payment?.total || 0);
      const totalFrete = parseFloat(firstItem.shipping?.price || fullData.shipping?.price || 0);
      if (mappedItems.length === 1 && mappedItems[0].valor_unitario_comercial === 0 && totalPayment > 0) {
        const deducedPrice = Math.max(0, totalPayment - totalFrete);
        mappedItems[0].valor_unitario_comercial = deducedPrice;
        mappedItems[0].valor_bruto = deducedPrice;
      }

      setItemsList(mappedItems);
      setValorFrete(totalFrete);
      setValorTotal(totalPayment || mappedItems.reduce((acc, it) => acc + (it.valor_unitario_comercial * it.quantidade_comercial), 0) + totalFrete);

      alert(`Dados do pedido #${orderNum} carregados com sucesso!`);
    } catch (err) {
      console.error(err);
      alert("Erro ao buscar pedido no sistema: " + err.message);
    } finally {
      setSearchingOrder(false);
    }
  };

  // Atualiza CFOP e Natureza quando muda o tipo de nota avulsa
  const handleAvulsoTipoNotaChange = (newType) => {
    setAvulsoTipoNota(newType);
    let nature = "Venda de mercadoria";
    let defaultCfop = "5102";

    if (newType === "devolucao") {
      nature = "Devolução de venda de mercadoria";
      defaultCfop = "1202";
    } else if (newType === "garantia_entrada") {
      nature = "Retorno de mercadoria recebida para conserto ou garantia";
      defaultCfop = "1915";
    } else if (newType === "garantia_saida") {
      nature = "Remessa em devolução de mercadoria recebida para conserto ou garantia";
      defaultCfop = "5916";
    }

    setNaturezaOperacao(nature);

    const emitenteUf = "SP";
    const itemUf = destData.uf.trim().toUpperCase();

    setItemsList((prev) =>
      prev.map((item) => {
        let computedCfop = defaultCfop;
        if (itemUf) {
          if (newType === "devolucao") {
            computedCfop = itemUf === emitenteUf ? "1202" : "2202";
          } else if (newType === "garantia_entrada") {
            computedCfop = itemUf === emitenteUf ? "1915" : "2915";
          } else if (newType === "garantia_saida") {
            computedCfop = itemUf === emitenteUf ? "5916" : "6916";
          } else {
            computedCfop = itemUf === emitenteUf ? "5102" : "6102";
          }
        }
        return {
          ...item,
          cfop: computedCfop,
        };
      })
    );
  };

  // --- ESTADO DOS DADOS FISCAIS ---
  const [destData, setDestData] = useState({
    nome: "",
    cpf_cnpj: "",
    email: "",
    telefone: "",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    uf: "",
    ie: "",
    indicador_ie: "9",
  });

  const [itemsList, setItemsList] = useState([]);
  const [valorFrete, setValorFrete] = useState(0);
  const [valorTotal, setValorTotal] = useState(0);

  const fetchAddressFromCep = async (rawCep) => {
    const cleanCep = rawCep.replace(/\D/g, "");
    if (cleanCep.length !== 8) return;

    try {
      const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      if (res.ok) {
        const data = await res.json();
        if (!data.erro) {
          setDestData((prev) => ({
            ...prev,
            logradouro: prev.logradouro || data.logradouro || "",
            bairro: prev.bairro || data.bairro || "",
            cidade: prev.cidade || data.localidade || "",
            uf: prev.uf || (data.uf || "").toUpperCase(),
          }));
        }
      }
    } catch (err) {
      console.error("Erro ao buscar CEP no ViaCEP:", err);
    }
  };

  // --- RESETAR ESTADO AO FECHAR O MODAL ---
  useEffect(() => {
    if (!isOpen) {
      setDestData({
        nome: "",
        cpf_cnpj: "",
        email: "",
        telefone: "",
        cep: "",
        logradouro: "",
        numero: "",
        complemento: "",
        bairro: "",
        cidade: "",
        uf: "",
        ie: "",
        indicador_ie: "9",
      });
      setItemsList([]);
      setValorFrete(0);
      setValorTotal(0);
      setNfeReferenciada("");
      setSearchedOrderId("");
      setSearchedOrderNumber("");
      setSearchOrderQuery("");
    }
  }, [isOpen]);

  // --- CARREGAR CONFIGURAÇÕES ---
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const data = await fiscalService.fetchConfigs();
        setConfigs(data);
        if (data.length > 0) {
          const origCnpj = invoice?.full_data?.cnpj_emissor || "";
          const match = data.find(c => c.cnpj.replace(/\D/g, "") === origCnpj.replace(/\D/g, ""));
          if (match) {
            setSelectedConfigId(match.id);
          } else {
            setSelectedConfigId(data[0].id);
          }
        }
      } catch (e) {
        console.error("Erro ao carregar configurações fiscais:", e);
      }
    };
    if (isOpen) {
      fetchConfigs();
    }
  }, [isOpen, invoice]);

  // --- PREENCHER DADOS DO PEDIDO / NOTA EXISTENTE ---
  useEffect(() => {
    if (!isOpen) return;

    const loadInvoiceData = async () => {
      if (invoice) {
        let focusPayload = invoice.focus_payload || invoice.full_data?.focus_payload || {};
        let fullData = invoice.full_data || {};
        let order = invoice.expand?.order || invoice.order || fullData.order || {};
        let customer = order.customer || invoice.customer || fullData.customer || {};
        let shipping = order.shipping || invoice.shipping || fullData.shipping || {};
        let address = customer.address || shipping.modified_address || shipping.address || order.shipping_address || fullData.shipping_address || {};

        const originalId = invoice.venda_original || fullData.venda_original_id || invoice.venda_original_id;
        if ((!address.street || !customer.cpf) && originalId) {
          try {
            // Se for do tipo novo, buscamos a nota fiscal original, se for do antigo, buscamos a ordem
            const origRecord = await pb.collection("fiscal_invoices").getOne(originalId).catch(() => null) ||
                               await pb.collection("production_orders").getOne(originalId).catch(() => null);
            if (origRecord) {
              const origFocusPayload = origRecord.focus_payload || origRecord.full_data?.focus_payload || {};
              const origFullData = origRecord.full_data || {};
              focusPayload = { ...origFocusPayload, ...focusPayload };
              fullData = { ...origFullData, ...fullData };
              const origOrder = origRecord.expand?.order || origRecord.order || origFullData.order || {};
              order = origOrder.customer ? origOrder : order;
              customer = order.customer || origRecord.customer || origFullData.customer || customer;
              shipping = order.shipping || origRecord.shipping || origFullData.shipping || shipping;
              address = customer.address || shipping.modified_address || shipping.address || order.shipping_address || origFullData.shipping_address || address;
            }
          } catch (err) {
            console.error("Erro ao carregar dados do pedido original para reenvio:", err);
          }
        }

        const isDev = invoice.tipo_nota === "devolucao" || fullData.is_devolucao === true;
        const isGar = (invoice.tipo_nota && invoice.tipo_nota.startsWith("garantia")) || fullData.is_garantia === true;
        const isGarantiaEntrada = invoice.tipo_nota === "garantia_entrada" || fullData.is_garantia_entrada === true;
        const statecode = (address.statecode || address.state || "").toUpperCase().trim();

        let initialTipoNota = "venda";
        if (isDev) {
          initialTipoNota = "devolucao";
        } else if (isGar) {
          if (isGarantiaEntrada) {
            initialTipoNota = "garantia_entrada";
          } else {
            initialTipoNota = "garantia_saida";
          }
        }
        setAvulsoTipoNota(initialTipoNota);

        const refChave = invoice.nfe_chave_referenciada || 
                         fullData.nfe_referenciada || 
                         focusPayload.notas_referenciadas?.[0]?.chave_nfe || 
                         fullData.focus_payload?.notas_referenciadas?.[0]?.chave_nfe || "";
        setNfeReferenciada(refChave);

        setDestData({
          nome: focusPayload.nome_destinatario || invoice.customer_name || customer.name || "",
          cpf_cnpj: focusPayload.cpf_destinatario || focusPayload.cnpj_destinatario || customer.cpf || customer.cnpj || "",
          email: focusPayload.email_destinatario || customer.email || "",
          telefone: focusPayload.telefone_destinatario || customer.phone || "",
          cep: focusPayload.cep_destinatario || address.zip || "",
          logradouro: focusPayload.logradouro_destinatario || address.street || "",
          numero: focusPayload.numero_destinatario || address.number || "",
          complemento: focusPayload.complemento_destinatario || address.complemento || "",
          bairro: focusPayload.bairro_destinatario || address.bairro || "",
          cidade: focusPayload.municipio_destinatario || address.city || "",
          uf: (focusPayload.uf_destinatario || address.statecode || address.state || "").toUpperCase(),
          ie: focusPayload.inscricao_estadual_destinatario === "ISENTO" ? "" : (focusPayload.inscricao_estadual_destinatario || ""),
          indicador_ie: String(focusPayload.indicador_inscricao_estadual_destinatario || (focusPayload.inscricao_estadual_destinatario === "ISENTO" ? "2" : (focusPayload.inscricao_estadual_destinatario ? "1" : "9"))),
        });

        setValorFrete(parseFloat(focusPayload.valor_frete || shipping.price || 0));
        setValorTotal(parseFloat(
          focusPayload.valor_total || 
          invoice.total_value || 
          invoice.valor_total ||
          fullData.valor_total || 
          order.payment?.total || 
          0
        ));

        let mappedItems = [];
        if (focusPayload.itens && focusPayload.itens.length > 0) {
          mappedItems = focusPayload.itens.map((item, idx) => {
            let price = parseFloat(item.valor_unitario_comercial || 0);
            const itemSku = item.codigo_produto || invoice.sku || fullData.sku || "";
            const catalogItem = findCatalogItem ? findCatalogItem(itemSku) : null;
            
            if (price === 0) {
              if (catalogItem && catalogItem.price) {
                price = parseFloat(catalogItem.price);
              } else if (focusPayload.valor_total) {
                price = parseFloat(focusPayload.valor_total) / focusPayload.itens.length;
              } else if (order.payment?.total) {
                price = parseFloat(order.payment.total) / focusPayload.itens.length;
              }
            }

            return {
              id: invoice.id,
              numero_item: item.numero_item || (idx + 1),
              codigo_produto: itemSku,
              descricao: item.descricao || catalogItem?.name || "Produto Sem Nome",
              quantidade_comercial: parseFloat(item.quantidade_comercial || 1),
              valor_unitario_comercial: price,
              valor_bruto: price * parseFloat(item.quantidade_comercial || 1),
              ncm: item.codigo_ncm || catalogItem?.ncm || "71131100",
              cfop: item.cfop || (isDev ? (statecode === "SP" ? "1202" : "2202") : (isGar ? (isGarantiaEntrada ? (statecode === "SP" ? "1915" : "2915") : (statecode === "SP" ? "5916" : "6916")) : "")),
              origem: item.icms_origem || 0,
              csosn: item.icms_situacao_tributaria || "102",
              pis_cst: item.pis_situacao_tributaria || "07",
              cofins_cst: item.cofins_situacao_tributaria || "07",
            };
          });
        } else {
          const catalogItem = findCatalogItem ? findCatalogItem(invoice.sku || fullData.sku) : null;
          let price = parseFloat(invoice.total_value || invoice.valor_total || fullData.valor_total || 0);
          
          if (price === 0) {
            if (catalogItem && catalogItem.price) {
              price = parseFloat(catalogItem.price);
            } else if (order.payment?.total) {
              price = parseFloat(order.payment.total || 0);
            }
          }

          const specsObj = invoice.specs || fullData.specs || {};
          if (specsObj) {
            Object.values(specsObj).forEach(specVal => {
              if (typeof specVal === "string") {
                const match = specVal.match(/R\$\s*([0-9.,]+)/);
                if (match) {
                  const valStr = match[1].replace(/\./g, "").replace(",", ".");
                  price += parseFloat(valStr) || 0;
                }
              }
            });
          }

          mappedItems = [{
            id: invoice.id,
            numero_item: 1,
            codigo_produto: invoice.sku || fullData.sku || "",
            descricao: catalogItem?.name || fullData.descricao || invoice.sku || "Produto Sem Nome",
            quantidade_comercial: parseFloat(invoice.quantity || fullData.quantity || 1),
            valor_unitario_comercial: price,
            valor_bruto: price * parseFloat(invoice.quantity || fullData.quantity || 1),
            ncm: catalogItem?.ncm || fullData.ncm || "71131100",
            cfop: isDev ? (statecode === "SP" ? "1202" : "2202") : (isGar ? (isGarantiaEntrada ? (statecode === "SP" ? "1915" : "2915") : (statecode === "SP" ? "5916" : "6916")) : ""),
            origem: 0,
            csosn: "102",
            pis_cst: "07",
            cofins_cst: "07",
          }];
        }
        setItemsList(mappedItems);

        const rawCep = focusPayload.cep_destinatario || address.zip;
        const rawBairro = focusPayload.bairro_destinatario || address.bairro;
        if (rawCep && (!rawBairro || !rawBairro.trim())) {
          fetchAddressFromCep(rawCep);
        }
      }
    };

    loadInvoiceData();
  }, [isOpen, invoice, findCatalogItem]);

  // --- CARREGAR PEDIDO EM GRUPO (Caso contrário) ---
  useEffect(() => {
    if (!isOpen || invoice) return;
    
    if (orderGroup && orderGroup.items.length > 0) {
      const refItem = orderGroup.items[0];
      
      setAvulsoTipoNota(forcedTipoNota || "venda");

      try {
        const orderNum = orderGroup.orderNumber;
        if (orderNum) {
          setSearchingReferencedKey(true);
          pb.collection("fiscal_invoices").getList(1, 1, {
            filter: `order_number = "${String(orderNum).trim()}" && nfe_status = "autorizada"`,
            sort: "-created",
          }).then((res) => {
            if (res.items.length > 0) {
              setNfeReferenciada(res.items[0].nfe_chave || "");
            } else if (refItem.expand?.fiscal_invoices_via_order) {
              const authInv = refItem.expand.fiscal_invoices_via_order.find(i => i.nfe_status === "autorizada");
              if (authInv) {
                setNfeReferenciada(authInv.nfe_chave || "");
              }
            }
          })
          .catch(err => console.warn("Erro ao buscar nota referenciada:", err))
          .finally(() => setSearchingReferencedKey(false));
        }
      } catch (err) {}

      const safeOrder = refItem.order || {};
      const safeCust = safeOrder.customer || {};
      const safeShipping = refItem.shipping || {};
      const safeAddr = safeShipping.address || safeOrder.shipping_address || {};

      const frete = parseFloat(safeShipping.price || 0);
      const total = parseFloat(orderGroup.totalValue || safeOrder.payment?.total || 0);

      // Dados Destinatário
      setDestData({
        nome: safeCust.name || orderGroup.customerName || "",
        cpf_cnpj: safeCust.cpf || safeCust.cnpj || "",
        email: safeCust.email || "",
        telefone: safeCust.phone || "",
        cep: safeAddr.zip || "",
        logradouro: safeAddr.street || "",
        numero: safeAddr.number || "",
        complemento: safeAddr.complemento || "",
        bairro: safeAddr.bairro || "",
        cidade: safeAddr.city || "",
        uf: (safeAddr.statecode || safeAddr.state || "").toUpperCase(),
        ie: safeCust.ie || "",
        indicador_ie: safeCust.cpf ? "9" : (safeCust.cnpj ? "9" : "9"),
      });

      setValorFrete(frete);
      setValorTotal(total);

      // Mapeia os Itens do Pedido
      const mappedItems = orderGroup.items.map((item, idx) => {
        const catalogItem = findCatalogItem ? findCatalogItem(item.sku) : null;
        const itemName = catalogItem?.name || item.name || "Produto Sem Nome";
        let itemPrice = parseFloat(item.price || catalogItem?.price || 0);

        // Soma os valores adicionais dos specs (ex: "45cm - R$ 45,00")
        if (item.specs) {
          Object.values(item.specs).forEach(specVal => {
            if (typeof specVal === "string") {
              const match = specVal.match(/R\$\s*([0-9.,]+)/);
              if (match) {
                const valStr = match[1].replace(/\./g, "").replace(",", ".");
                itemPrice += parseFloat(valStr) || 0;
              }
            }
          });
        }

        return {
          id: item.id,
          numero_item: idx + 1,
          codigo_produto: item.sku || "",
          descricao: itemName,
          quantidade_comercial: 1.0, // Cada linha representa uma joia unitária na confecção
          valor_unitario_comercial: itemPrice,
          valor_bruto: itemPrice,
          ncm: catalogItem?.ncm || item.ncm || "", // Será preenchido com o NCM da config fiscal se vazio
          cfop: "", // Calculado depois com base no Estado
          origem: 0, // Nacional
          csosn: "102", // Simples Nacional
          pis_cst: "01",
          cofins_cst: "01",
        };
      });

      // Se houver apenas 1 item e o preço dele veio zerado, tenta deduzir do valor total do pedido
      if (mappedItems.length === 1 && mappedItems[0].valor_unitario_comercial === 0) {
        const deducedPrice = Math.max(0, total - frete);
        mappedItems[0].valor_unitario_comercial = deducedPrice;
        mappedItems[0].valor_bruto = deducedPrice;
      }

      setItemsList(mappedItems);

      // Auto-busca do CEP se estiver preenchido mas o bairro estiver vazio
      if (safeAddr.zip && (!safeAddr.bairro || !safeAddr.bairro.trim())) {
        fetchAddressFromCep(safeAddr.zip);
      }
    } else if (isAvulso) {
      setDestData({
        nome: "",
        cpf_cnpj: "",
        email: "",
        telefone: "",
        cep: "",
        logradouro: "",
        numero: "",
        complemento: "",
        bairro: "",
        cidade: "",
        uf: "",
      });
      setValorFrete(0);
      setValorTotal(0);
      setItemsList([
        {
          id: Date.now() + Math.random(),
          numero_item: 1,
          codigo_produto: "",
          descricao: "",
          quantidade_comercial: 1.0,
          valor_unitario_comercial: 0,
          valor_bruto: 0,
          ncm: "",
          cfop: "",
          origem: 0,
          csosn: "102",
          pis_cst: "07",
          cofins_cst: "07",
        }
      ]);
    }
  }, [isOpen, orderGroup, invoice, findCatalogItem, isAvulso, forcedTipoNota]);

  // --- MONITORAR UF E CONFIGURAÇÃO SELECIONADA PARA ATUALIZAR IMPOSIÇÕES DE CFOP/NCM ---
  useEffect(() => {
    if (!selectedConfigId || configs.length === 0) return;
    const config = configs.find((c) => c.id === selectedConfigId);
    if (!config) return;

    // A UF do emitente (SP) costuma ser a base
    const emitenteUf = "SP"; // Padrão Sempre Joias (Waldemar Carlos Pereira - SP)
    const isDevolucao = avulsoTipoNota === "devolucao";
    const isGarantia = avulsoTipoNota === "garantia_entrada" || avulsoTipoNota === "garantia_saida";
    const isGarantiaEntrada = avulsoTipoNota === "garantia_entrada";
    
    setItemsList((prevItems) =>
      prevItems.map((item) => {
        const itemUf = destData.uf.trim().toUpperCase();
        let computedCfop = item.cfop;
        if (itemUf) {
          if (isDevolucao) {
            computedCfop = itemUf === emitenteUf ? "1202" : "2202";
          } else if (isGarantia) {
            if (isGarantiaEntrada) {
              computedCfop = itemUf === emitenteUf ? "1915" : "2915";
            } else {
              computedCfop = itemUf === emitenteUf ? "5916" : "6916";
            }
          } else {
            computedCfop = itemUf === emitenteUf ? "5102" : "6102";
          }
        }
        const rawNcm = (item.ncm || config.ncm_padrao || "71131100").replace(/\D/g, "");
        const computedNcm = rawNcm === "711311000" ? "71131100" : (rawNcm || "71131100");

        return {
          ...item,
          cfop: computedCfop,
          ncm: computedNcm,
        };
      })
    );
  }, [selectedConfigId, configs, destData.uf, invoice, isAvulso, avulsoTipoNota]);
  // Auto-ajusta a Natureza da Operação com base no Tipo de Nota
  useEffect(() => {
    if (avulsoTipoNota === "venda") {
      setNaturezaOperacao("Venda de mercadoria");
    } else if (avulsoTipoNota === "devolucao") {
      setNaturezaOperacao("Devolucao de venda de mercadoria");
    } else if (avulsoTipoNota === "garantia_entrada") {
      setNaturezaOperacao("Retorno de mercadoria para conserto ou garantia");
    } else if (avulsoTipoNota === "garantia_saida") {
      setNaturezaOperacao("Remessa para conserto ou manutencao");
    }
  }, [avulsoTipoNota]);


  // Auto-soma para emissão avulsa
  useEffect(() => {
    if (isAvulso) {
      const sum = itemsList.reduce((acc, item) => acc + (item.valor_unitario_comercial * item.quantidade_comercial), 0);
      setValorTotal(parseFloat((sum + valorFrete).toFixed(2)));
    }
  }, [itemsList, valorFrete, isAvulso]);

  // --- VALIDAÇÃO DE CAMPOS OBRIGATÓRIOS ---
  useEffect(() => {
    const errors = [];
    if (!destData.nome.trim()) {
      errors.push("Nome do cliente é obrigatório.");
    } else if (destData.nome.trim().length > 60) {
      errors.push("Nome do cliente não pode exceder 60 caracteres (SEFAZ).");
    }
    
    const cleanCpfCnpj = destData.cpf_cnpj.replace(/\D/g, "");
    if (!cleanCpfCnpj) {
      errors.push("CPF/CNPJ do destinatário é obrigatório.");
    } else if (cleanCpfCnpj.length !== 11 && cleanCpfCnpj.length !== 14) {
      errors.push("CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos.");
    }

    if (!destData.cep.replace(/\D/g, "")) errors.push("CEP é obrigatório.");
    if (!destData.logradouro.trim()) errors.push("Logradouro (Rua) é obrigatório.");
    if (!destData.numero.trim()) errors.push("Número do endereço é obrigatório.");
    if (!destData.bairro.trim()) errors.push("Bairro é obrigatório.");
    if (!destData.cidade.trim()) errors.push("Cidade é obrigatória.");
    
    if (!destData.uf.trim()) {
      errors.push("Estado (UF) é obrigatório.");
    } else if (destData.uf.trim().length !== 2) {
      errors.push("UF deve conter exatamente 2 caracteres.");
    }

    if (cleanCpfCnpj.length === 14 && destData.indicador_ie === "1") {
      if (!destData.ie || !destData.ie.trim()) {
        errors.push("Inscrição Estadual (IE) é obrigatória para Contribuintes ICMS.");
      }
    }

    if (avulsoTipoNota !== "venda") {
      const cleanRef = nfeReferenciada.replace(/\D/g, "");
      if (!cleanRef) {
        errors.push("Chave NF-e Referenciada é obrigatória para notas de devolução/garantia.");
      } else if (cleanRef.length !== 44) {
        errors.push("Chave NF-e Referenciada deve conter exatamente 44 dígitos.");
      }
    }

    if (itemsList.length === 0) {
      errors.push("A nota precisa ter pelo menos um item.");
    }

    itemsList.forEach((item, idx) => {
      if (!item.codigo_produto || !item.codigo_produto.trim()) {
        errors.push(`Item #${idx + 1}: SKU/Código é obrigatório.`);
      }
      if (!item.descricao || !item.descricao.trim()) {
        errors.push(`Item #${idx + 1}: Descrição é obrigatória.`);
      } else if (item.descricao.trim() === "Produto Sem Nome") {
        errors.push(`Item #${idx + 1}: Por favor, preencha a descrição do produto.`);
      }
      if (typeof item.valor_unitario_comercial !== "number" || item.valor_unitario_comercial <= 0) {
        errors.push(`Item #${idx + 1}: Preço unitário deve ser maior que zero.`);
      }
      const cleanNcm = (item.ncm || "").replace(/\D/g, "");
      if (!cleanNcm) {
        errors.push(`Item #${idx + 1}: NCM é obrigatório.`);
      } else if (cleanNcm.length !== 8) {
        errors.push(`Item #${idx + 1}: NCM inválido (${item.ncm}). O NCM deve conter exatamente 8 dígitos (ex: 71131100).`);
      }
      if (!item.cfop || !item.cfop.trim()) {
        errors.push(`Item #${idx + 1}: CFOP é obrigatório.`);
      }
    });

    setValidationErrors(errors);
  }, [destData, itemsList, isAvulso, avulsoTipoNota, nfeReferenciada]);

  if (!isOpen) return null;

  const handleFieldChange = (field, value) => {
    setDestData((prev) => ({ ...prev, [field]: value }));
    if (field === "cep") {
      fetchAddressFromCep(value);
    }
  };

  const handleItemChange = (idx, field, value) => {
    setItemsList((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handleEmit = async () => {
    if (validationErrors.length > 0) {
      alert("Por favor, corrija os erros de validação antes de prosseguir.");
      return;
    }

    const config = configs.find((c) => c.id === selectedConfigId);
    if (!config) {
      alert("Configuração fiscal não encontrada.");
      return;
    }

    setLoading(true);
    try {
      // 1. Calcula o desconto e frete e distribui de forma proporcional para evitar rejeições da SEFAZ
      let adjustedItems = itemsList.map(item => ({ ...item }));
      let sumItems = adjustedItems.reduce((acc, item) => acc + (item.valor_unitario_comercial * item.quantidade_comercial), 0);
      let calculatedDiscount = (sumItems + valorFrete) - valorTotal;

      // Se o desconto calculado for negativo (acréscimo), ajustamos o preço dos itens para cima para evitar rejeições
      if (calculatedDiscount < 0) {
        const adjustment = Math.abs(calculatedDiscount);
        const lastIdx = adjustedItems.length - 1;
        adjustedItems[lastIdx].valor_unitario_comercial = parseFloat((adjustedItems[lastIdx].valor_unitario_comercial + adjustment).toFixed(2));
        adjustedItems[lastIdx].valor_bruto = parseFloat((adjustedItems[lastIdx].valor_unitario_comercial * adjustedItems[lastIdx].quantidade_comercial).toFixed(2));
        
        // Recalcula somatório e zera o desconto
        sumItems = adjustedItems.reduce((acc, item) => acc + (item.valor_unitario_comercial * item.quantidade_comercial), 0);
        calculatedDiscount = 0;
      }

      let discountPerItem = [];
      if (calculatedDiscount > 0) {
        let remainingDiscount = calculatedDiscount;
        discountPerItem = adjustedItems.map((item, idx) => {
          if (idx === adjustedItems.length - 1) {
            return parseFloat(remainingDiscount.toFixed(2));
          }
          const itemShare = (item.valor_unitario_comercial * item.quantidade_comercial) / sumItems;
          const itemDiscount = parseFloat((calculatedDiscount * itemShare).toFixed(2));
          remainingDiscount -= itemDiscount;
          return itemDiscount;
        });
      }

      let freightPerItem = [];
      if (valorFrete > 0) {
        let remainingFreight = valorFrete;
        freightPerItem = adjustedItems.map((item, idx) => {
          if (idx === adjustedItems.length - 1) {
            return parseFloat(remainingFreight.toFixed(2));
          }
          const itemShare = (item.valor_unitario_comercial * item.quantidade_comercial) / sumItems;
          const itemFreight = parseFloat((valorFrete * itemShare).toFixed(2));
          remainingFreight -= itemFreight;
          return itemFreight;
        });
      }

      const cleanCpfCnpj = destData.cpf_cnpj.replace(/\D/g, "");
      const isCnpj = cleanCpfCnpj.length === 14;
      
      const isDevolucao = avulsoTipoNota === "devolucao";
      const isGarantia = avulsoTipoNota === "garantia_entrada" || avulsoTipoNota === "garantia_saida";
      const isGarantiaEntrada = avulsoTipoNota === "garantia_entrada";
      
      const originalChave = nfeReferenciada.replace(/\D/g, "");
      const user = pb.authStore.model?.name || pb.authStore.model?.email || "Sistema";

      const focus_payload = {
        cnpj_emitente: config.cnpj.replace(/\D/g, ""),
        inscricao_estadual_emitente: config.inscricao_estadual.replace(/\D/g, ""),
        regime_tributario_emitente: 1, // Simples Nacional
        data_emissao: new Date().toISOString(),
        tipo_documento: (isDevolucao || isGarantiaEntrada) ? 0 : 1, // Entrada se for devolução ou garantia de entrada, senão Saída
        finalidade_emissao: isDevolucao ? 4 : 1, // Devolução se for devolução, senão Normal
        consumidor_final: 1, // Sim
        presenca_comprador: 2, // Internet
        natureza_operacao: ((naturezaOperacao || "").trim() || (isDevolucao ? "Devolucao de venda de mercadoria" : (isGarantia ? (isGarantiaEntrada ? "Retorno de mercadoria recebida para conserto ou garantia" : "Remessa para conserto ou manutencao") : "Venda de mercadoria"))).substring(0, 60),
        modalidade_frete: (isDevolucao || isGarantia) ? 9 : (valorFrete > 0 ? 0 : 9), 
        
        nome_destinatario: destData.nome.trim().substring(0, 60),
        [isCnpj ? "cnpj_destinatario" : "cpf_destinatario"]: cleanCpfCnpj,
        ...(isCnpj ? {
          indicador_inscricao_estadual_destinatario: parseInt(destData.indicador_ie || "9"),
          inscricao_estadual_destinatario: destData.indicador_ie === "1" ? (destData.ie || "").replace(/\D/g, "") : (destData.indicador_ie === "2" ? "ISENTO" : undefined),
        } : {
          indicador_inscricao_estadual_destinatario: 9,
        }),
        email_destinatario: destData.email.trim() || undefined,
        telefone_destinatario: destData.telefone.replace(/\D/g, "") || undefined,
        
        logradouro_destinatario: destData.logradouro.trim(),
        numero_destinatario: destData.numero.trim(),
        complemento_destinatario: destData.complemento.trim() || undefined,
        bairro_destinatario: destData.bairro.trim(),
        municipio_destinatario: destData.cidade.trim(),
        uf_destinatario: destData.uf.trim().toUpperCase(),
        cep_destinatario: destData.cep.replace(/\D/g, ""),

        ...((isDevolucao || isGarantia) && originalChave ? {
          notas_referenciadas: [
            {
              chave_nfe: originalChave
            }
          ]
        } : {}),

        itens: adjustedItems.map((item, idx) => {
          const itemDisc = discountPerItem[idx] || 0;
          const itemFreight = freightPerItem[idx] || 0;
          return {
            numero_item: item.numero_item,
            codigo_produto: item.codigo_produto,
            descricao: item.descricao,
            unidade_comercial: "UN",
            quantidade_comercial: item.quantidade_comercial,
            valor_unitario_comercial: item.valor_unitario_comercial,
            valor_bruto: item.valor_bruto,
            valor_desconto: itemDisc > 0 ? itemDisc : undefined,
            valor_frete: itemFreight > 0 ? itemFreight : undefined,
            unidade_tributavel: "UN",
            quantidade_tributavel: item.quantidade_comercial,
            valor_unitario_tributavel: item.valor_unitario_comercial,
            codigo_ncm: (item.ncm || "").replace(/\D/g, ""),
            cfop: item.cfop,
            icms_origem: item.origem,
            icms_situacao_tributaria: isDevolucao ? "900" : (isGarantia ? (item.csosn || "400") : (item.csosn || "102")), 
            pis_situacao_tributaria: (isDevolucao || isGarantia) ? "07" : (item.pis_cst || "07"),
            cofins_situacao_tributaria: (isDevolucao || isGarantia) ? "07" : (item.cofins_cst || "07")
          };
        }),

        valor_desconto: calculatedDiscount > 0 ? parseFloat(calculatedDiscount.toFixed(2)) : undefined,
        valor_frete: valorFrete > 0 ? valorFrete : undefined,
        valor_total: valorTotal,
      };

      // 1. Criar ou Atualizar Registro na coleção fiscal_invoices
      let fiscalRecordId = "";
      
      const orderId = isAvulso 
        ? (searchedOrderId || undefined)
        : (invoice?.order || invoice?.full_data?.order || (orderGroup && orderGroup.items.length > 0 ? orderGroup.items[0].id : undefined));
      const targetOrderNumber = isAvulso 
        ? (searchedOrderNumber || "AVULSO")
        : (invoice ? (invoice.order_number || invoice.full_data?.order_number) : (orderGroup ? orderGroup.orderNumber : ""));

      const tipoNotaVal = avulsoTipoNota;

      const dbPayload = {
        order: orderId || null,
        order_number: targetOrderNumber,
        customer_name: destData.nome.trim().substring(0, 60),
        cnpj_emissor: config.cnpj.replace(/\D/g, ""),
        ambiente: config.ambiente,
        tipo_nota: tipoNotaVal,
        nfe_status: "processando",
        usuario_emissor: user,
        focus_payload: focus_payload,
        focus_ref: "",
        venda_original: invoice?.venda_original || invoice?.full_data?.venda_original_id || null,
      };

      if (invoice && invoice.id && !invoice.order_number) { // Se o invoice recebido já é da coleção nova
        dbPayload.focus_ref = invoice.id;
        await pb.collection("fiscal_invoices").update(invoice.id, dbPayload);
        fiscalRecordId = invoice.id;
      } else {
        // Criar novo registro
        const newRecord = await pb.collection("fiscal_invoices").create(dbPayload);
        fiscalRecordId = newRecord.id;
        // Atualizar focus_ref com o próprio ID do registro recém-criado
        await pb.collection("fiscal_invoices").update(fiscalRecordId, { focus_ref: fiscalRecordId });
      }

      // Vincular como active_invoice no(s) pedido(s)
      if (isAvulso && searchedOrderNumber) {
        try {
          // Find all production_orders with this order_number and update active_invoice
          const relatedOrders = await pb.collection("production_orders").getFullList({
            filter: `order_number = "${searchedOrderNumber}"`
          });
          for (const item of relatedOrders) {
            await pb.collection("production_orders").update(item.id, {
              active_invoice: fiscalRecordId,
            });
          }
        } catch (err) {
          console.error(`Erro ao vincular active_invoice na ordem avulsa:`, err);
        }
      } else if (orderGroup && orderGroup.items) {
        for (const item of orderGroup.items) {
          try {
            await pb.collection("production_orders").update(item.id, {
              active_invoice: fiscalRecordId,
            });
          } catch (err) {
            console.error(`Erro ao vincular active_invoice na ordem ${item.id}:`, err);
          }
        }
      } else if (orderId) {
        try {
          await pb.collection("production_orders").update(orderId, {
            active_invoice: fiscalRecordId,
          });
        } catch (err) {
          console.error(`Erro ao vincular active_invoice na ordem ${orderId}:`, err);
        }
      }

      // Se for uma venda do PDV, tenta atualizar o status da venda no pdv_sales
      if (targetOrderNumber && targetOrderNumber.startsWith("PDV-")) {
        try {
          const pdvSales = await pb.collection("pdv_sales").getFullList({
            filter: `order_number = "${targetOrderNumber}"`
          });
          if (pdvSales.length > 0) {
            await pb.collection("pdv_sales").update(pdvSales[0].id, {
              status: "PDV_SINCRONIZADO", // Marca como sincronizado após emitir nota
              synced: true,
            });
          }
        } catch (errPdv) {
          console.warn("Erro ao atualizar sincronização da venda PDV após nota fiscal:", errPdv);
        }
      }

      // 2. Disparar Webhook do n8n
      const token = (config.ambiente === "producao" ? config.token_producao : config.token_homologacao || "").trim();

      const n8n_payload = {
        ref: fiscalRecordId,
        orderNumber: targetOrderNumber,
        cnpj_emissor: config.cnpj,
        ambiente: config.ambiente,
        token: token,
        pb_token: pb.authStore.token,
        items: isAvulso ? [] : (orderGroup ? orderGroup.items.map((i) => ({ id: i.id })) : [{ id: orderId }]),
        focus_payload: focus_payload,
      };

      await fiscalService.requestInvoice(n8n_payload);

      // Salva vínculo na nota original retroativamente se necessário
      const originalId = invoice?.venda_original || invoice?.full_data?.venda_original_id;
      if (originalId && (tipoNotaVal === "devolucao" || tipoNotaVal.startsWith("garantia"))) {
        try {
          const keyToUpdate = tipoNotaVal === "devolucao" ? "devolucao_id" : "garantia_id";
          const origRecord = await pb.collection("fiscal_invoices").getOne(originalId).catch(() => null);
          if (origRecord) {
            const origFullData = origRecord.full_data || {};
            await pb.collection("fiscal_invoices").update(originalId, {
              full_data: {
                ...origFullData,
                [keyToUpdate]: fiscalRecordId
              }
            });
          }
        } catch (err) {
          console.error("Erro ao atualizar vínculo retroativo na nota original:", err);
        }
      }
      
      alert("Requisição de nota fiscal enviada com sucesso para processamento!");
      if (onSuccess) onSuccess();
      onClose();
    } catch (e) {
      console.error(e);
      let errMsg = e.message;
      if (e.data && e.data.data) {
        const details = Object.entries(e.data.data)
          .map(([key, val]) => `${key}: ${val.message || JSON.stringify(val)}`)
          .join(", ");
        errMsg += ` (Detalhes: ${details})`;
      }

      // Rollback do status para erro se falhar
      try {
        if (fiscalRecordId) {
          await pb.collection("fiscal_invoices").update(fiscalRecordId, {
            nfe_status: "erro",
            nfe_error_message: errMsg
          });
          if (onSuccess) onSuccess();
        }
      } catch (updateErr) {
        console.error("Erro ao marcar nota como erro no PocketBase:", updateErr);
      }

      alert("Erro ao disparar emissão da nota fiscal: " + errMsg);
    } finally {
      setLoading(false);
    }
  };

  const sumItems = itemsList.reduce((acc, item) => acc + (item.valor_unitario_comercial * item.quantidade_comercial), 0);
  const diff = (sumItems + valorFrete) - valorTotal;
  const isCnpj = destData.cpf_cnpj.replace(/\D/g, "").length === 14;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="bg-blue-600 p-4 flex justify-between items-center text-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <FileText size={24} />
            </div>
            <div>
              <h2 className="font-bold text-lg leading-tight">
                {isAvulso ? "Nova Emissão Avulsa (NF-e)" : (invoice ? "Corrigir e Reemitir Nota Fiscal (NF-e)" : "Emitir Nota Fiscal (NF-e)")}
              </h2>
              <p className="text-blue-100 text-xs">
                {isAvulso ? "Emissão Avulsa" : ("Pedido #" + (invoice ? (invoice.order_number || invoice.full_data?.order_number) : orderGroup?.orderNumber))} • {isAvulso ? itemsList.length : (invoice ? itemsList.length : orderGroup?.items?.length)} item(ns)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-white/20 p-2 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar space-y-6 flex-1">
          {configs.length === 0 ? (
            <div className="text-center py-10 bg-slate-50 border rounded-xl border-dashed">
              <Building2 size={40} className="mx-auto text-slate-350 mb-2" />
              <p className="text-sm font-bold text-slate-500">Nenhum CNPJ Emissor Configurado</p>
              <p className="text-xs text-slate-400 mt-1 mb-3">Vá na aba Configurações &gt; Configurações Fiscais para cadastrar um CNPJ emissor.</p>
            </div>
          ) : (
            <>
              {/* Seleção do Emissor */}
              <div className="border rounded-xl p-4 bg-slate-55 bg-slate-50/50 border-slate-200">
                <h3 className="font-bold text-blue-800 text-xs uppercase mb-3 flex items-center gap-2">
                  <Building2 size={14} /> Empresa Emitente (CNPJ)
                </h3>
                <div className="flex gap-4 flex-wrap">
                  {configs.map((c) => (
                    <label
                      key={c.id}
                      className={`flex-1 min-w-[200px] p-3 border rounded-xl cursor-pointer transition-all flex items-center gap-3 bg-white ${
                        selectedConfigId === c.id
                          ? "border-blue-500 ring-2 ring-blue-50 bg-blue-50/10"
                          : "border-slate-200 hover:border-slate-350"
                      }`}
                    >
                      <input
                        type="radio"
                        name="selectedConfig"
                        value={c.id}
                        checked={selectedConfigId === c.id}
                        onChange={() => setSelectedConfigId(c.id)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <div className="font-bold text-slate-800 text-xs leading-tight">{c.razao_social}</div>
                        <div className="text-[10px] text-slate-450 font-mono mt-0.5">{c.cnpj}</div>
                        <span className={`inline-block text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full mt-1 border ${
                          c.ambiente === "producao" ? "bg-orange-50 text-orange-600 border-orange-100" : "bg-blue-50 text-blue-600 border-blue-100"
                        }`}>
                          {c.ambiente}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Configurações de Emissão de Nota */}
              <div className="border rounded-xl p-4 bg-slate-55 bg-slate-50/50 border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {isAvulso ? (
                  /* Busca do Pedido - Apenas em modo avulso */
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Importar Dados de Pedido</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Nº do Pedido (Ex: 69584)"
                        value={searchOrderQuery}
                        onChange={(e) => setSearchOrderQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            fetchOrderDetailsForAvulso(searchOrderQuery);
                          }
                        }}
                        className="flex-1 p-2 border border-slate-200 bg-white rounded-lg text-xs outline-none focus:border-blue-500 font-bold"
                      />
                      <button
                        type="button"
                        onClick={() => fetchOrderDetailsForAvulso(searchOrderQuery)}
                        disabled={searchingOrder}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-1 active:scale-95 disabled:bg-blue-300 cursor-pointer"
                      >
                        {searchingOrder ? "Buscando..." : "Importar"}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Em modo de pedido normal, mostramos um campo informativo */
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Origem dos Dados</label>
                    <div className="p-2.5 border border-slate-200 bg-slate-100/50 rounded-lg text-xs font-mono font-bold text-slate-600">
                      Pedido #{invoice ? (invoice.order_number || invoice.full_data?.order_number) : orderGroup?.orderNumber}
                    </div>
                  </div>
                )}

                {/* Tipo de Nota */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Tipo de Nota Fiscal</label>
                  <select
                    value={avulsoTipoNota}
                    onChange={(e) => handleAvulsoTipoNotaChange(e.target.value)}
                    className="w-full p-2.5 border border-slate-200 bg-white rounded-lg text-xs font-bold text-slate-800 outline-none focus:border-blue-500 transition-all cursor-pointer"
                  >
                    <option value="venda">Venda de Mercadoria (Saída)</option>
                    <option value="devolucao">Devolução/Cancelamento (Entrada)</option>
                    <option value="garantia_entrada">Entrada para Garantia (Entrada)</option>
                    <option value="garantia_saida">Reenvio de Garantia (Saída)</option>
                  </select>
                </div>

                {/* Nota Referenciada (Chave NF-e) */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                    Chave NF-e Referenciada {avulsoTipoNota !== "venda" && <span className="text-red-500 font-black">*</span>}
                  </label>
                  <input
                    type="text"
                    maxLength="60"
                    placeholder="Chave original com 44 dígitos"
                    value={nfeReferenciada}
                    onChange={(e) => {
                      const cleanVal = e.target.value.replace(/\D/g, "").slice(0, 44);
                      setNfeReferenciada(cleanVal);
                    }}
                    required={avulsoTipoNota !== "venda"}
                    disabled={avulsoTipoNota === "venda"}
                    className={`w-full p-2 border border-slate-200 bg-white rounded-lg text-xs outline-none focus:border-blue-500 font-mono font-bold ${
                      avulsoTipoNota === "venda" ? "bg-slate-100/50 cursor-not-allowed text-slate-400" : ""
                    }`}
                  />
                  {avulsoTipoNota !== "venda" && (
                    <div className="mt-1 leading-tight text-[9px] font-bold">
                      {searchingReferencedKey ? (
                        <span className="text-blue-600 animate-pulse">
                          Buscando Nota Fiscal autorizada no histórico do pedido...
                        </span>
                      ) : nfeReferenciada ? (
                        <span className="text-green-600">
                          Chave importada automaticamente do histórico do pedido.
                        </span>
                      ) : (
                        <span className="text-amber-600">
                          Nenhuma Nota Fiscal anterior encontrada no sistema. Digite manualmente a chave de 44 dígitos.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Erros de Validação */}
              {validationErrors.length > 0 && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                  <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={18} />
                  <div>
                    <h4 className="font-bold text-red-800 text-xs uppercase">Dados Fiscais Faltantes / Inválidos</h4>
                    <ul className="text-xs text-red-700 list-disc list-inside mt-1.5 space-y-1">
                      {validationErrors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Dados do Destinatário */}
              <div className="border rounded-xl p-4 bg-white border-slate-200">
                <h3 className="font-bold text-slate-700 text-xs uppercase mb-3 flex items-center gap-2">
                  <User size={14} /> Dados do Destinatário (Cliente)
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <div className="flex justify-between items-center">
                      <label className="text-[10px] font-bold text-slate-500 uppercase">Nome Completo</label>
                      {destData.nome && destData.nome.length >= 50 && (
                        <span className={`text-[9px] font-bold uppercase tracking-wider ${destData.nome.length >= 60 ? "text-red-500 animate-pulse" : "text-amber-500"}`}>
                          {destData.nome.length}/60 Caracteres
                        </span>
                      )}
                    </div>
                    <input
                      className={`w-full p-2 border rounded text-xs outline-none transition-colors ${destData.nome && destData.nome.length >= 60 ? "border-red-300 focus:border-red-500" : "focus:border-blue-500"}`}
                      value={destData.nome}
                      maxLength="60"
                      onChange={(e) => handleFieldChange("nome", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase">CPF ou CNPJ (Apenas Números)</label>
                    <input
                      className="w-full p-2 border rounded text-xs outline-none focus:border-blue-500 font-mono"
                      value={destData.cpf_cnpj}
                      onChange={(e) => handleFieldChange("cpf_cnpj", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Telefone / Zap</label>
                    <input
                      className="w-full p-2 border rounded text-xs outline-none focus:border-blue-500"
                      value={destData.telefone}
                      onChange={(e) => handleFieldChange("telefone", e.target.value)}
                    />
                  </div>
                </div>

                {isCnpj && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 pt-3 border-t border-slate-100 animate-fade-in">
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 uppercase text-blue-600">Tipo de Contribuinte (Indicador de IE)</label>
                      <select
                        className="w-full p-2 border rounded text-xs outline-none focus:border-blue-500 bg-white font-bold"
                        value={destData.indicador_ie || "9"}
                        onChange={(e) => handleFieldChange("indicador_ie", e.target.value)}
                      >
                        <option value="9">Não Contribuinte (Venda para Consumidor Final, sem IE)</option>
                        <option value="1">Contribuinte ICMS (Possui Inscrição Estadual)</option>
                        <option value="2">Contribuinte Isento (Isento de Inscrição no Cadastro de Contribuintes)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-500 uppercase">Inscrição Estadual (IE)</label>
                      <input
                        className={`w-full p-2 border rounded text-xs outline-none focus:border-blue-500 font-mono font-bold ${
                          (destData.indicador_ie || "9") !== "1" ? "bg-slate-100 cursor-not-allowed text-slate-400" : ""
                        }`}
                        placeholder={destData.indicador_ie === "2" ? "ISENTO" : "Somente números"}
                        value={destData.indicador_ie === "1" ? (destData.ie || "") : (destData.indicador_ie === "2" ? "ISENTO" : "")}
                        disabled={(destData.indicador_ie || "9") !== "1"}
                        onChange={(e) => handleFieldChange("ie", e.target.value.replace(/\D/g, ""))}
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3">
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Email</label>
                    <input
                      className="w-full p-2 border rounded text-xs outline-none focus:border-blue-500"
                      value={destData.email}
                      onChange={(e) => handleFieldChange("email", e.target.value)}
                    />
                  </div>
                  {isAvulso && (
                    <div className="md:col-span-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase text-blue-600">Natureza da Operação</label>
                      <input
                        className="w-full p-2 border rounded text-xs outline-none focus:border-blue-500 font-bold"
                        value={naturezaOperacao}
                        onChange={(e) => setNaturezaOperacao(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Endereço de Entrega */}
              <div className="border rounded-xl p-4 bg-white border-slate-200">
                <h3 className="font-bold text-slate-700 text-xs uppercase mb-3 flex items-center gap-2">
                  <MapPin size={14} /> Endereço de Entrega
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase">CEP</label>
                    <input
                      className="w-full p-2 border rounded text-xs font-mono"
                      value={destData.cep}
                      onChange={(e) => handleFieldChange("cep", e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Logradouro / Rua</label>
                    <input
                      className="w-full p-2 border rounded text-xs"
                      value={destData.logradouro}
                      onChange={(e) => handleFieldChange("logradouro", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Número</label>
                    <input
                      className="w-full p-2 border rounded text-xs"
                      value={destData.numero}
                      onChange={(e) => handleFieldChange("numero", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Complemento</label>
                    <input
                      className="w-full p-2 border rounded text-xs"
                      value={destData.complemento}
                      onChange={(e) => handleFieldChange("complemento", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Bairro</label>
                    <input
                      className="w-full p-2 border rounded text-xs"
                      value={destData.bairro}
                      onChange={(e) => handleFieldChange("bairro", e.target.value)}
                    />
                  </div>
                  <div className="col-span-2 md:col-span-4">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Cidade</label>
                    <input
                      className="w-full p-2 border rounded text-xs"
                      value={destData.cidade}
                      onChange={(e) => handleFieldChange("cidade", e.target.value)}
                    />
                  </div>
                  <div className="col-span-2 md:col-span-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase text-red-600">UF (Estado)</label>
                    <input
                      className="w-full p-2 border rounded text-xs font-bold text-center uppercase"
                      maxLength={2}
                      value={destData.uf}
                      onChange={(e) => handleFieldChange("uf", e.target.value.toUpperCase())}
                    />
                  </div>
                </div>
              </div>

              {/* Itens do Pedido Fiscal */}
              <div className="border rounded-xl p-4 bg-white border-slate-200">
                <h3 className="font-bold text-slate-700 text-xs uppercase mb-3 flex items-center gap-2">
                  <FileText size={14} /> Especificações Fiscais dos Itens
                </h3>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b text-slate-500 uppercase">
                        <th className="py-2 pr-2">SKU</th>
                        <th className="py-2 pr-2">Descrição</th>
                        <th className="py-2 pr-2 text-right w-20">Qtd</th>
                        <th className="py-2 pr-2 text-right">Preço Unit</th>
                        <th className="py-2 pr-2 text-center w-24">NCM</th>
                        <th className="py-2 text-center w-16">CFOP</th>
                        {isAvulso && <th className="py-2 text-center w-12">Ação</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {itemsList.map((item, idx) => (
                        <tr key={item.id} className="border-b hover:bg-slate-50">
                          <td className="py-2.5 pr-2 w-28">
                            {isAvulso ? (
                              <input
                                className="w-full p-1 border rounded text-xs font-mono uppercase"
                                placeholder="SKU"
                                value={item.codigo_produto || ""}
                                onChange={(e) => handleItemChange(idx, "codigo_produto", e.target.value.toUpperCase())}
                              />
                            ) : (
                              <span className="font-mono font-bold text-blue-600">{item.codigo_produto}</span>
                            )}
                          </td>
                          <td className="py-2.5 pr-2 max-w-[220px]">
                            <input
                              className="w-full p-1 border rounded text-xs"
                              value={item.descricao}
                              onChange={(e) => handleItemChange(idx, "descricao", e.target.value)}
                            />
                          </td>
                          <td className="py-2.5 pr-2 text-right">
                            {isAvulso ? (
                              <input
                                type="number"
                                min="1"
                                step="1"
                                className="w-16 p-1 border rounded text-center text-xs font-mono font-medium"
                                value={item.quantidade_comercial}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 1;
                                  handleItemChange(idx, "quantidade_comercial", val);
                                  handleItemChange(idx, "valor_bruto", val * item.valor_unitario_comercial);
                                }}
                              />
                            ) : (
                              <span className="font-mono">{item.quantidade_comercial}</span>
                            )}
                          </td>
                          <td className="py-2.5 pr-2">
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-slate-400">R$</span>
                              <input
                                type="number"
                                step="0.01"
                                className="w-20 p-1 border rounded text-right text-xs font-mono"
                                value={item.valor_unitario_comercial}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  handleItemChange(idx, "valor_unitario_comercial", val);
                                  handleItemChange(idx, "valor_bruto", val);
                                }}
                              />
                            </div>
                          </td>
                          <td className="py-2.5 px-2">
                            <input
                              className="w-full p-1 border rounded text-center text-xs font-mono"
                              value={item.ncm}
                              onChange={(e) => handleItemChange(idx, "ncm", e.target.value)}
                            />
                          </td>
                          <td className="py-2.5 pl-2">
                            <input
                              className="w-full p-1 border rounded text-center text-xs font-mono font-bold"
                              maxLength={4}
                              value={item.cfop}
                              onChange={(e) => handleItemChange(idx, "cfop", e.target.value)}
                            />
                          </td>
                          {isAvulso && (
                            <td className="py-2.5 text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  setItemsList((prev) => prev.filter((_, i) => i !== idx));
                                }}
                                className="p-1.5 text-slate-450 hover:text-red-650 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                title="Remover Item"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {isAvulso && (
                  <button
                    type="button"
                    onClick={() => {
                      setItemsList((prev) => [
                        ...prev,
                        {
                          id: Date.now() + Math.random(),
                          numero_item: prev.length + 1,
                          codigo_produto: "",
                          descricao: "",
                          quantidade_comercial: 1,
                          valor_unitario_comercial: 0,
                          valor_bruto: 0,
                          ncm: configs.find((c) => c.id === selectedConfigId)?.ncm_padrao || "71131100",
                          cfop: destData.uf.trim().toUpperCase() === "SP" ? "5102" : "6102",
                          origem: 0,
                          csosn: "102",
                          pis_cst: "07",
                          cofins_cst: "07",
                        },
                      ]);
                    }}
                    className="mt-3 px-3 py-1.5 border border-dashed border-blue-300 hover:border-blue-500 text-blue-600 hover:bg-blue-50 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer w-fit"
                  >
                    <Plus size={14} />
                    Adicionar Item
                  </button>
                )}

                {diff < -0.01 && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-start gap-2">
                    <AlertTriangle className="shrink-0 text-amber-600 mt-0.5" size={16} />
                    <div>
                      <span className="font-bold">Aviso de Incongruência de Valor (Acréscimo):</span> A soma dos itens + frete (R$ {(sumItems + valorFrete).toFixed(2)}) é menor que o valor pago pelo cliente (R$ {valorTotal.toFixed(2)}). O sistema ajustará automaticamente **+ R$ {Math.abs(diff).toFixed(2)}** no último item para corresponder ao total da nota, ou você pode ajustar os preços na tabela acima.
                    </div>
                  </div>
                )}
                {diff > 0.01 && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700 flex items-start gap-2">
                    <FileText className="shrink-0 text-blue-500 mt-0.5" size={16} />
                    <div>
                      <span className="font-bold">Desconto Identificado:</span> Será aplicado um desconto proporcional de **R$ {diff.toFixed(2)}** sobre os itens da nota fiscal.
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-6 mt-4 pt-3 border-t border-slate-100 text-xs items-center">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Truck size={14} /> Custo Envio:
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400">R$</span>
                      <input
                        type="number"
                        step="0.01"
                        className="w-20 p-1 border rounded text-right text-xs font-mono font-medium"
                        value={valorFrete}
                        onChange={(e) => setValorFrete(parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 font-bold text-slate-800 text-sm">
                    Total:
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400">R$</span>
                      <input
                        type="number"
                        step="0.01"
                        className="w-24 p-1 border rounded text-right text-xs font-mono font-bold text-emerald-650"
                        value={valorTotal}
                        onChange={(e) => setValorTotal(parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-3 bg-slate-55 bg-slate-50 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-500 font-bold hover:bg-slate-200 rounded-lg text-sm"
          >
            Fechar
          </button>
          {configs.length > 0 && (
            <button
              onClick={handleEmit}
              disabled={loading || validationErrors.length > 0}
              className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-lg flex items-center gap-2 text-sm disabled:bg-blue-300 disabled:cursor-not-allowed cursor-pointer"
            >
              <Send size={16} /> Confirmar & Emitir Nota Fiscal
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
